import React from 'react'
import { Badge } from '@enamelai/design-system'
import { Icon as MailIcon } from '@enamelai/design-system/icons/Mail_fill'
import { Icon as MessageIcon } from '@enamelai/design-system/icons/Message'
import { Icon as FaxIcon } from '@enamelai/design-system/icons/Fax'
import { Icon as CallIcon } from '@enamelai/design-system/icons/Call'
import {
  ICommunicationHistory,
  CommunicationItemType,
  CommunicationStatus,
} from '@dataTypes/communicationHistory.type'
import { formatItemTitle } from '@utils/communicationHistory.util'

const TYPE_ICONS: Record<
  CommunicationItemType,
  React.ComponentType<React.SVGProps<SVGSVGElement>>
> = {
  Email: MailIcon,
  SMS: MessageIcon,
  Fax: FaxIcon,
  Calls: CallIcon,
}

type ProfileCommunicationHistoryRowProps = {
  item: ICommunicationHistory
  onClick?: (item: ICommunicationHistory) => void
}

function ProfileCommunicationHistoryRow({
  item,
  onClick,
}: ProfileCommunicationHistoryRowProps): React.ReactElement {
  const isMissed = item.status === CommunicationStatus.Missed
  const isInteractive = Boolean(onClick)
  const TypeIcon = TYPE_ICONS[item.type]

  const handleClick = (): void => {
    onClick?.(item)
  }

  const handleKeyDown = (event: React.KeyboardEvent): void => {
    if (!onClick) return
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      onClick(item)
    }
  }

  return (
    <article
      className={`patient-profile-communications-row${
        isInteractive ? ' patient-profile-communications-row--clickable' : ''
      }`}
      role={isInteractive ? 'button' : undefined}
      tabIndex={isInteractive ? 0 : undefined}
      onClick={isInteractive ? handleClick : undefined}
      onKeyDown={isInteractive ? handleKeyDown : undefined}
    >
      <div className="patient-profile-communications-row-main">
        <span
          className={`patient-profile-communications-icon patient-profile-communications-icon--${item.type}`}
          aria-hidden
        >
          {TypeIcon ? <TypeIcon className="patient-profile-icon" /> : null}
        </span>
        <div className="patient-profile-communications-content">
          <h3 className="patient-profile-communications-title">
            {formatItemTitle(item)}
          </h3>
          <p className="patient-profile-communications-meta-line">
            {item.patientName}
          </p>
          <p className="patient-profile-communications-subject">{item.text}</p>
        </div>
      </div>
      <div className="patient-profile-communications-aside">
        <time
          className="patient-profile-communications-timestamp"
          dateTime={item.timestamp}
        >
          {item.timestamp}
        </time>
        <Badge
          mode="tinted"
          color={isMissed ? 'red' : 'vivid-green'}
          label={item.status}
        />
      </div>
    </article>
  )
}

export default ProfileCommunicationHistoryRow
